import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Nav } from '../components/Nav';
import { UnifiedFooter, SectionHeading } from '../components';
import '../styles/global.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { posts, BlogPost } from '../posts';
import { Helmet } from 'react-helmet';

// Add this to fix TS errors for window.hbspt
declare global {
  interface Window {
    hbspt?: any;
  }
}

// Modal component for HubSpot form
const HubSpotModal: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      // Only load the script if the modal is open
      const scriptId = 'hs-form-embed-script';
      if (!document.getElementById(scriptId)) {
        const script = document.createElement('script');
        script.src = 'https://js.hsforms.net/forms/embed/48854903.js';
        script.defer = true;
        script.id = scriptId;
        document.body.appendChild(script);
      }
    }
  }, [open]);

  // Remove form content when modal closes (for re-mounting)
  useEffect(() => {
    if (!open && modalRef.current) {
      modalRef.current.innerHTML = '';
    }
  }, [open]);

  useEffect(() => {
    if (open && modalRef.current) {
      // Insert the HubSpot form div
      modalRef.current.innerHTML = '<div class="hs-form-frame" data-region="na1" data-form-id="443964e0-d2a6-46e8-b7ea-4a28e60ea177" data-portal-id="48854903"></div>';
      // If the script is already loaded, re-run the form embed
      if (window.hbspt && window.hbspt.forms) {
        window.hbspt.forms.create({
          region: 'na1',
          portalId: '48854903',
          formId: '443964e0-d2a6-46e8-b7ea-4a28e60ea177',
          target: modalRef.current.querySelector('.hs-form-frame'),
        });
      }
    }
  }, [open]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg max-w-lg w-full p-6 relative flex flex-col items-center"
        onClick={e => e.stopPropagation()}
      >
        <button
          className="absolute top-4 right-4 flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition text-2xl text-gray-500 hover:text-gray-700 focus:outline-none"
          onClick={onClose}
          aria-label="Close"
          type="button"
        >
          ×
        </button>
        <h2 className="text-xl font-semibold mb-4 text-center">Download our free whitepaper</h2>
        <div ref={modalRef} className="w-full" />
      </div>
    </div>
  );
};

const CTABox: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <div className="mt-16 bg-white border border-blue-100 rounded-2xl p-8 sm:p-10 mb-14 flex flex-col items-center text-center shadow-md max-w-2xl mx-auto">
    <span className="font-semibold text-2xl sm:text-3xl mb-4 text-blue-900">Want a deeper dive?</span>
    <span className="mb-7 text-lg sm:text-xl text-dark-700">
      Download our free whitepaper for a detailed breakdown of attack techniques and lateral movement, incident response case studies, and defense.
    </span>
    <button
      className="bg-blue-700 text-white px-8 py-3 rounded-xl hover:bg-blue-800 transition font-semibold text-lg sm:text-xl shadow focus:outline-none focus:ring-2 focus:ring-blue-400"
      onClick={onClick}
      type="button"
    >
      Download Free Whitepaper
    </button>
  </div>
);

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post: BlogPost | undefined = posts.find((p: BlogPost) => p.slug === slug);
  const [modalOpen, setModalOpen] = useState(false);

  // Custom link renderer for ReactMarkdown
  const components = {
    a: (props: any) => {
      // Join all children as text for robust matching
      const linkText = React.Children.toArray(props.children).join('').toLowerCase();
      if (linkText.includes('download our free whitepaper')) {
        return (
          <button
            type="button"
            className="underline text-blue-700 hover:text-blue-900 cursor-pointer bg-transparent border-none p-0 m-0 inline"
            onClick={() => setModalOpen(true)}
          >
            {props.children}
          </button>
        );
      }
      if (linkText.includes('schedule a call')) {
        return (
          <a
            href="https://zcal.co/neilpatil/syntradiscovery"
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-blue-700 hover:text-blue-900 cursor-pointer"
          >
            {props.children}
          </a>
        );
      }
      // Default link behavior
      return <a {...props} />;
    },
  };

  return (
    <div className="min-h-screen bg-white text-dark-900 font-sans overflow-x-clip">
      {post && (
        <Helmet>
          <title>{post.title} | Blog</title>
          <meta name="description" content={post.description} />
          <meta property="og:title" content={post.title} />
          <meta property="og:description" content={post.description} />
          <meta property="og:type" content="article" />
          <meta property="og:image" content={post.avatar} />
          <meta property="og:url" content={window.location.href} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={post.title} />
          <meta name="twitter:description" content={post.description} />
          <meta name="twitter:image" content={post.avatar} />
        </Helmet>
      )}
      {post && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.description,
            "author": { "@type": "Person", "name": post.author },
            "datePublished": post.date,
            "image": post.avatar,
            "mainEntityOfPage": window.location.href,
          })
        }} />
      )}
      <Nav activeLink="blog" />
      <div className="pt-12">
        <div className="container-medium">
          {post ? (
            <article className="max-w-3xl mx-auto py-24">
              <Link to="/blog" className="text-blue-700 text-sm font-mono uppercase tracking-wider mb-8 inline-block hover:underline">← Back to Blog</Link>
              <SectionHeading
                caption={post.category}
                title={<span className="leading-tight">{post.title}</span>}
                description={post.description}
                className="text-left mb-8"
              />
              <div className="flex items-center mb-8">
                <img src={post.avatar} alt={post.author} className="w-10 h-10 rounded-full mr-3" />
                <span className="text-dark-700 font-medium mr-4">{post.author}</span>
                <span className="text-gray-400 text-sm">{post.date}</span>
              </div>
              <div className="blog-markdown prose prose-lg max-w-none text-dark-700 leading-relaxed">
                {post.markdown ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{post.markdown}</ReactMarkdown>
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: post.content || '' }} />
                )}
              </div>
              <CTABox onClick={() => setModalOpen(true)} />
            </article>
          ) : (
            <div className="max-w-2xl mx-auto py-32 text-center">
              <h2 className="text-3xl font-semibold mb-4">Post not found</h2>
              <p className="mb-8">Sorry, we couldn't find the blog post you're looking for.</p>
              <CTABox onClick={() => setModalOpen(true)} />
              <Link to="/blog" className="text-blue-700 hover:underline">← Back to Blog</Link>
            </div>
          )}
        </div>
      </div>
      <UnifiedFooter />
      <HubSpotModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default BlogPostPage; 